/*
 ============================================================
  ADMIN API — all Supabase calls used by the admin panel
  Reuses the single shared client from backend/lib.
 ============================================================
*/
import { supabase } from '../../backend/lib/supabaseClient';

export { supabase };

/* Detect "table does not exist" so pages can show setup help
   instead of a generic error. */
export const isMissingTable = (error) =>
  !!error &&
  (error.code === '42P01' || // undefined_table
    error.code === 'PGRST205' || // not in schema cache
    /relation .* does not exist/i.test(error.message || '') ||
    /could not find the table/i.test(error.message || ''));

/* Same idea for an RPC whose SQL file hasn't been run yet. PostgREST
   answers "Could not find the function ... in the schema cache", which
   means nothing to a shop owner — pages use this to name the file to
   run instead. */
export const isMissingFunction = (error) =>
  !!error &&
  (error.code === '42883' || // undefined_function
    error.code === 'PGRST202' ||
    /could not find the function/i.test(error.message || '') ||
    /function .* does not exist/i.test(error.message || ''));

/* =========================
   ACCOUNT
========================= */
export const getCurrentUser = async () => {
  const { data, error } = await supabase.auth.getUser();
  return { user: data?.user || null, error };
};

/* Change the signed-in admin's own password.
   Supabase doesn't require the old password, so we re-authenticate
   first — otherwise anyone on an unattended session could lock the
   real owner out. */
export const changePassword = async ({ email, currentPassword, newPassword }) => {
  const { error: reauthError } = await supabase.auth.signInWithPassword({
    email,
    password: currentPassword,
  });

  if (reauthError) {
    return { error: { message: 'Current password is incorrect.' } };
  }

  const { error } = await supabase.auth.updateUser({ password: newPassword });
  return { error };
};

/* Emails a reset link. Requires Site URL + redirect URLs to be
   configured in Supabase → Authentication → URL Configuration. */
export const sendPasswordReset = async (email) => {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/admin/login`,
  });
  return { error };
};

/* =========================
   DASHBOARD
========================= */
export const getDashboardStats = async () => {
  const [orders, users, products] = await Promise.all([
    supabase
      .from('orders')
      .select('id, final_amount, order_status, payment_status, created_at'),
    supabase.from('users').select('id', { count: 'exact', head: true }),
    supabase
      .from('products')
      .select('id, name, stock_quantity, in_stock, is_active'),
  ]);

  const orderRows = orders.data || [];
  const active = orderRows.filter((o) => o.order_status !== 'cancelled');
  const revenue = active
    .filter((o) => o.payment_status === 'paid')
    .reduce((sum, o) => sum + Number(o.final_amount || 0), 0);
  const pendingOrders = orderRows.filter(
    (o) => o.order_status === 'pending'
  ).length;

  const productRows = products.data || [];
  const lowStock = productRows.filter(
    (p) => p.is_active && Number(p.stock_quantity) <= 3
  );

  return {
    totalOrders: orderRows.length,
    pendingOrders,
    revenue,
    totalUsers: users.count || 0,
    totalProducts: productRows.filter((p) => p.is_active).length,
    lowStock,
    error: orders.error || users.error || products.error || null,
  };
};

export const getRecentOrders = async (limit = 8) => {
  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);
  return { data: data || [], error };
};

/* =========================
   PRODUCTS
========================= */
export const getAdminProducts = async () => {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .order('created_at', { ascending: false });
  return { data: data || [], error };
};

export const createProduct = async (product) => {
  const { data, error } = await supabase
    .from('products')
    .insert(product)
    .select()
    .single();
  return { data, error };
};

export const updateProduct = async (id, updates) => {
  const { data, error } = await supabase
    .from('products')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  return { data, error };
};

export const deleteProduct = async (id) => {
  const { error } = await supabase.from('products').delete().eq('id', id);
  return { error };
};

/* Upload an image file to the "product-images" storage bucket
   and return its public URL. */
export const uploadProductImage = async (file) => {
  const ext = file.name.split('.').pop();
  const path = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;

  const { error } = await supabase.storage
    .from('product-images')
    .upload(path, file, { cacheControl: '3600', upsert: false });

  if (error) return { url: null, error };

  const { data } = supabase.storage.from('product-images').getPublicUrl(path);
  return { url: data.publicUrl, error: null };
};

/* =========================
   ORDERS
========================= */
export const getOrders = async () => {
  const { data, error } = await supabase
    .from('orders')
    .select('*, order_items(*)')
    .order('created_at', { ascending: false });
  return { data: data || [], error };
};

/* Raise the invoice for an order.

   Everything happens server-side in one statement (see
   backend/sql/invoices.sql): the next invoice number is drawn, the
   order is marked paid, and the method/reference/date are stamped.
   Doing it here instead would mean the browser choosing invoice
   numbers, and two admins could pick the same one. */
export const issueInvoice = async (orderId, { method = 'UPI', reference = null } = {}) => {
  const { data, error } = await supabase.rpc('issue_invoice', {
    p_order_id: orderId,
    p_method: method,
    p_reference: reference,
  });

  // The function returns a single orders row.
  return { data: Array.isArray(data) ? data[0] : data, error };
};

/* Stamp an order as handed to the vendor.

   Deliberately forgiving: if vendor.sql hasn't been run the column is
   missing, and the dispatch message has already gone out by this
   point. Failing loudly here would suggest the send failed when it
   didn't — so the error is reported back and the caller shrugs. */
export const markVendorSent = async (id) => {
  const { error } = await supabase
    .from('orders')
    .update({ vendor_sent_at: new Date().toISOString() })
    .eq('id', id);

  return { error };
};

export const updateOrder = async (id, updates) => {
  const { data, error } = await supabase
    .from('orders')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  return { data, error };
};

/* =========================
   COUPONS

   Only admins can read this table at all (see backend/sql/coupons.sql)
   — customers check one code at a time through the validate_coupon()
   function, so the list of live codes never leaves the admin panel.
========================= */
export const getCoupons = async () => {
  const { data, error } = await supabase
    .from('coupons')
    .select('*')
    .order('created_at', { ascending: false });
  return { data: data || [], error };
};

export const createCoupon = async (coupon) => {
  const { data, error } = await supabase
    .from('coupons')
    .insert(coupon)
    .select()
    .single();
  return { data, error };
};

/* used_count is deliberately never sent from here: it is maintained by
   the order trigger, and an edit that overwrote it would hand back
   redemptions on a limited code. */
export const updateCoupon = async (id, updates) => {
  const { used_count, ...safe } = updates;

  const { data, error } = await supabase
    .from('coupons')
    .update(safe)
    .eq('id', id)
    .select()
    .single();
  return { data, error };
};

export const deleteCoupon = async (id) => {
  const { error } = await supabase.from('coupons').delete().eq('id', id);
  return { error };
};

/* =========================
   USERS
========================= */
export const getUsers = async () => {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .order('created_at', { ascending: false });
  return { data: data || [], error };
};

export const updateUser = async (id, updates) => {
  const { data, error } = await supabase
    .from('users')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  return { data, error };
};

/* =========================
   CONSULTATIONS
========================= */
export const getConsultations = async () => {
  const { data, error } = await supabase
    .from('consultations')
    .select('*')
    .order('created_at', { ascending: false });
  return { data: data || [], error };
};

export const updateConsultation = async (id, updates) => {
  const { data, error } = await supabase
    .from('consultations')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  return { data, error };
};

/* =========================
   REVIEWS
========================= */
export const getReviews = async () => {
  const { data, error } = await supabase
    .from('reviews')
    .select('*')
    .order('created_at', { ascending: false });
  return { data: data || [], error };
};

export const updateReview = async (id, updates) => {
  const { data, error } = await supabase
    .from('reviews')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  return { data, error };
};

export const deleteReview = async (id) => {
  const { error } = await supabase.from('reviews').delete().eq('id', id);
  return { error };
};

/* =========================
   NEWSLETTER
========================= */
export const getSubscribers = async () => {
  const { data, error } = await supabase
    .from('newsletter_subscribers')
    .select('*')
    .order('created_at', { ascending: false });
  return { data: data || [], error };
};

export const deleteSubscriber = async (id) => {
  const { error } = await supabase
    .from('newsletter_subscribers')
    .delete()
    .eq('id', id);
  return { error };
};

/* =========================
   BLOG
========================= */
export const getBlogPosts = async () => {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .order('created_at', { ascending: false });
  return { data: data || [], error };
};

export const createBlogPost = async (post) => {
  const { data, error } = await supabase
    .from('blog_posts')
    .insert(post)
    .select()
    .single();
  return { data, error };
};

export const updateBlogPost = async (id, updates) => {
  const { data, error } = await supabase
    .from('blog_posts')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  return { data, error };
};

export const deleteBlogPost = async (id) => {
  const { error } = await supabase.from('blog_posts').delete().eq('id', id);
  return { error };
};

/* Reuses the product-images bucket rather than adding a second one. */
export const uploadBlogImage = uploadProductImage;
